<template>
	<LayoutBreadcrumbs class="main-wrapper" />

	<div
		v-if="selectedProduct && Object.keys(selectedProduct).length"
		class="flex flex-col w-full mt-8 main-wrapper pb-8"
	>
		<div
			class="flex flex-wrap justify-between lg:gap-12 gap-6 flex-col md:flex-row w-full"
		>
			<div v-if="selectedProduct?.Files" class="md:w-[55%]">
				<StoryblokComponent
					v-for="blok in imageGallery"
					:key="blok._uid"
					:blok="blok"
					:images="
						selectedProduct.Files.length > 0
							? selectedProduct.Files
							: [defaultImage]
					"
					:price-list-id="
						specialPrice?.enabled ? selectedProduct.PriceListId : null
					"
					:special-price="specialPrice"
				/>
			</div>
			<LazyLoadWrapper>
				<StoryblokComponent
					v-for="blok in productAttributes"
					:key="blok._uid"
					:blok="blok"
					:parametrics="parametrics"
					:price-list-id="
						specialPrice?.enabled ? selectedProduct.PriceListId : null
					"
					:special-price="specialPrice"
					class="md:w-[40%] w-full"
				/>
			</LazyLoadWrapper>
		</div>
		<LazyLoadWrapper>
			<ProductPackageProducts
				v-if="selectedProduct"
				:product="selectedProduct"
				:is-item-blocked="isPackageItemBlocked"
			/>
			<ProductSimilarProducts
				:product-id="selectedProduct?.Id!"
				:show-add-to-cart="true"
			/>
			<StoryblokComponent
				v-for="blok in productDetails"
				:key="blok._uid"
				:blok="blok"
			/>
		</LazyLoadWrapper>
		<LazyLoadWrapper>
			<StoryblokComponent
				v-for="blok in productRecommendations"
				:key="blok._uid"
				:blok="blok"
			/>
		</LazyLoadWrapper>
	</div>
</template>

<script setup lang="ts">
import type {
	GroupedParametrics,
	ParametricListValue,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import type {
	BlokGenericForComponents,
	SpecialPrice
} from "~/types/storyblok/storyblok";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";

interface Props {
	parametrics: GroupedParametrics | undefined;
	imageGallery: BlokGenericForComponents[] | undefined;
	productAttributes: BlokGenericForComponents[] | undefined;
	productDetails: BlokGenericForComponents[] | undefined;
	colorFromParametricsInLink: ParametricListValue | undefined;
	specialPrice: SpecialPrice | null;
	productRecommendations: BlokGenericForComponents[] | undefined;
}

const {
	parametrics,
	imageGallery,
	productAttributes,
	productDetails,
	colorFromParametricsInLink,
	productRecommendations
} = defineProps<Props>();

const {
	getFirstSecondParametricWithStock,
	selectProductVariant,
	getSecondParametricName,
	getProductStock
} = useNorcePwa();
const { helloRetailTrack } = useHelloRetailTracking();
const { getStockStatus } = useStockStatus();

// Like the PDP button: out of stock stays buyable unless the stock status blocks it.
const isPackageItemBlocked = (item: ProductItem) =>
	getProductStock(item) < 1 &&
	!!getStockStatus(item.FlagIdSeed, false)?.disableAddToCart;

const { selectedProduct, products, selectedColor, selectedSecondParametric } =
	storeToRefs(useProductStore());
const productStore = useProductStore();
const defaultImage = computed(() => {
	return {
		id: "default-id",
		Key: "default-key",
		Name: selectedProduct?.value?.Name,
		Path: productStore.defaultProductImage // This will always have the latest value
	};
});

watch(selectedSecondParametric, () => {
	if (parametrics) {
		useProductStore().addSelectedProduct(
			selectProductVariant({
				selectedColorId: selectedColor.value?.Id,
				selectedSecondParametricId: selectedSecondParametric.value?.Id,
				products: products.value!,
				parametricsAvailable:
					parametrics?.color ??
					parametrics?.[getSecondParametricName(parametrics)]
			})
		);
	}
});

watch(selectedColor, () => {
	if (
		parametrics &&
		parametrics[getSecondParametricName(parametrics)] &&
		!colorFromParametricsInLink &&
		selectedColor.value
	) {
		const selectedSecondParametricLocal = getFirstSecondParametricWithStock({
			parametrics: parametrics[getSecondParametricName(parametrics)],
			products: products.value!,
			selectedColor: selectedColor.value
		});
		if (selectedSecondParametricLocal) {
			useProductStore().addSelectedSecondParametric(
				selectedSecondParametricLocal
			);
		}
	}
});

onMounted(() => {
	helloRetailTrack({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			productNumber: selectedProduct.value?.PartNo || "",
			referrer: window.location.origin
		}
	});
});
</script>
